<template>
  <div
    class="pb-1"
    :class="{
      'mb-4 border-bottom': !(form.isPhoneVerified && form.isEmailVerified) || form.isDriver,
    }"
  >
    <h5 class="text-dark fw-bold">
      {{ $t('master.user.contactInformation.title') }}
    </h5>
    <p class="text-muted mb-3">
      {{ $t('master.user.contactInformation.description') }}
    </p>

    <!-- Phone Number -->
    <div class="mb-4">
      <label class="form-label">
        {{ $t('master.user.phoneNumber') }}
      </label>
      <!-- Restructured layout: input and icons in separate container from error messages -->
      <div class="d-flex align-items-center">
        <div class="flex-grow-1">
          <McInputNumber
            id="phone_number_edit"
            :value="form.phoneNo"
            name="phoneNo"
            :invalidMsg="$t('master.user.phoneFormat')"
            :placeholder="$t('master.user.phoneNumber')"
            :disabled="
              (form.isPhoneVerified && !phoneEditEnabled) ||
              (isAnyFieldEditing && !phoneEditEnabled)
            "
            @onChange="(val) => $emit('update-form', { phoneNo: val })"
            @blur="$v.form.phoneNo.$touch()"
          />
        </div>
        <!-- Edit/Save/Cancel Buttons - tampil saat edit dan terverifikasi -->
        <!-- Removed padding-top, using align-items-center for perfect alignment -->
        <div v-if="form.isPhoneVerified" class="ms-2 d-flex">
          <!-- Edit Button -->
          <button
            v-if="!phoneEditEnabled"
            type="button"
            class="btn btn-link p-2 mc-user-modal__edit-btn"
            :title="$t('global.action.edit')"
            @click="$emit('enable-field-edit', 'phone')"
          >
            <i class="icon-outline_edit-2 mc-user-modal__edit-icon" />
          </button>
          <!-- Save & Cancel Buttons -->
          <template v-else>
            <button
              type="button"
              class="btn btn-link p-2 mc-user-modal__cancel-btn"
              :title="$t('global.action.cancel')"
              @click="$emit('cancel-field-edit', 'phone')"
            >
              <i class="icon-outline_close mc-user-modal__action-icon" />
            </button>
            <button
              type="button"
              class="btn btn-link p-2 mc-user-modal__save-btn"
              :title="$t('global.action.save')"
              @click="$emit('save-field-edit', 'phone')"
            >
              <i class="icon-outline_checkmark mc-user-modal__action-icon" />
            </button>
          </template>
        </div>
      </div>

      <!-- Moved error messages outside the flex container -->
      <div v-if="$v.form.phoneNo.$error" class="invalid-feedback d-block">
        <span v-if="!$v.form.phoneNo.minLength">
          {{ $t('master.user.validation.minLength', { min: 8 }) }}
        </span>
        <span v-else-if="!$v.form.phoneNo.maxLength">
          {{ $t('master.user.validation.maxLength', { max: 15 }) }}
        </span>
        <span v-else-if="!$v.form.phoneNo.phoneNumber">
          {{ $t('master.user.validation.phoneNumber') }}
        </span>
      </div>

      <!-- Phone Verification Badge - hanya tampil saat edit -->
      <div v-if="!phoneEditEnabled" class="mt-2">
        <span
          v-if="form.isPhoneVerified === true"
          class="mc-user-modal__verification-badge mc-user-modal__verification-badge--verified"
        >
          <i class="icon-fill_check-seal me-1" />
          {{ $t('master.user.verification.verified') }}
        </span>
        <span
          v-else-if="form.isPhoneVerified === false || form.phoneNo"
          class="mc-user-modal__verification-badge mc-user-modal__verification-badge--unverified"
        >
          <i class="icon-fill_alert-triangle me-1" />
          {{ $t('master.user.verification.notVerified') }}
        </span>
      </div>
    </div>

    <!-- Email -->
    <div class="mb-3">
      <label class="form-label">
        {{ $t('navbar.dropdown.megaMenu.application.list.email') }}
      </label>
      <!-- Restructured layout: input and icons in separate container from error messages -->
      <div class="d-flex align-items-center">
        <div class="flex-grow-1">
          <b-form-input
            :value="form.email"
            :placeholder="$t('master.user.placeholderEmail')"
            :disabled="
              (form.isEmailVerified && !emailEditEnabled) ||
              (isAnyFieldEditing && !emailEditEnabled)
            "
            :class="{
              'is-invalid': $v.form.email.$error,
            }"
            @input="$emit('update-form', { email: $event })"
            @blur="$v.form.email.$touch()"
          />
        </div>
        <!-- Edit/Save/Cancel Buttons - tampil saat edit dan terverifikasi -->
        <!-- Removed padding-top, using align-items-center for perfect alignment -->
        <div v-if="form.isEmailVerified" class="ms-2 d-flex">
          <!-- Edit Button -->
          <button
            v-if="!emailEditEnabled"
            type="button"
            class="btn btn-link p-2 mc-user-modal__edit-btn"
            :title="$t('global.action.edit')"
            @click="$emit('enable-field-edit', 'email')"
          >
            <i class="icon-outline_edit-2 mc-user-modal__edit-icon" />
          </button>
          <!-- Save & Cancel Buttons -->
          <template v-else>
            <button
              type="button"
              class="btn btn-link p-2 mc-user-modal__cancel-btn"
              :title="$t('global.action.cancel')"
              @click="$emit('cancel-field-edit', 'email')"
            >
              <i class="icon-outline_close mc-user-modal__action-icon" />
            </button>
            <button
              type="button"
              class="btn btn-link p-2 mc-user-modal__save-btn"
              :title="$t('global.action.save')"
              @click="$emit('save-field-edit', 'email')"
            >
              <i class="icon-outline_checkmark mc-user-modal__action-icon" />
            </button>
          </template>
        </div>
      </div>

      <!-- Moved error messages outside the flex container -->
      <div v-if="$v.form.email.$error" class="invalid-feedback d-block">
        <span v-if="!$v.form.email.maxLength">
          {{ $t('master.user.validation.maxLength', { max: 100 }) }}
        </span>
        <span v-else-if="!$v.form.email.email">
          {{ $t('master.user.validation.email') }}
        </span>
      </div>

      <McAlert
        v-if="emailEditEnabled"
        class="mt-2 mb-3 gap-0 align-items-md-center mc-user-modal__password-alert"
        variant="warning"
        :html="$t('master.user.editEmail.cautionText')"
        iconVariant="fill"
        withIcon
      />
      <!-- Email Verification Badge - hanya tampil saat edit -->
      <div v-else class="mt-2">
        <span
          v-if="form.isEmailVerified === true"
          class="mc-user-modal__verification-badge mc-user-modal__verification-badge--verified"
        >
          <i class="icon-fill_check-seal me-1" />
          {{ $t('master.user.verification.verified') }}
        </span>
        <span
          v-else-if="form.isEmailVerified === false || form.email"
          class="mc-user-modal__verification-badge mc-user-modal__verification-badge--unverified"
        >
          <i class="icon-fill_alert-triangle me-1" />
          {{ $t('master.user.verification.notVerified') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { email, minLength, maxLength } from 'vuelidate/lib/validators'
import McInputNumber from '@/components/McInputNumber'
import McAlert from '@/components/McAlert.vue'

// Custom phone number validator
const phoneNumber = (value) => {
  if (!value) return true

  const cleanPhone = value.replace(/\D/g, '')

  // Pola:
  // 1. Boleh diawali 0 lalu diikuti [1-9]...
  // 2. Atau diawali kode negara (1–3 digit) tapi setelahnya tidak boleh '0'
  const pattern = /^(0[1-9]\d{7,11}|[1-9]\d{0,2}[1-9]\d{6,10})$/

  return pattern.test(cleanPhone)
}

export default {
  name: 'ContactInformationSection',

  components: {
    McAlert,
    McInputNumber,
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    phoneEditEnabled: {
      type: Boolean,
      default: false,
    },
    emailEditEnabled: {
      type: Boolean,
      default: false,
    },
    isAnyFieldEditing: {
      type: Boolean,
      default: false,
    },
  },

  validations: {
    form: {
      email: {
        email,
        maxLength: maxLength(100),
      },
      phoneNo: {
        minLength: minLength(8),
        maxLength: maxLength(15),
        phoneNumber,
      },
    },
  },
}
</script>
